// Conversation screen layout used across pages.
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useHasCapability } from 'util/admin';
import { useIsEmbedded, useIsInSidebar, useSiloKey } from 'util/datastore';
import { TLabel, t } from 'util/stdtypes';

import { Banner } from './banner';
import { AdaptiveWidth, Center, HorizBox, Pad, PadBox } from './basics';
import { colorBorderPrimary, colorSurfaceHighlight, colorSurfacePrimary, colorTextAccent } from './color';
import { Heading, UtilityText, WebLinkTextButton } from './text';

export type ConversationScreenProps = { children: React.ReactNode; backgroundColor?: string; pad?: boolean; noWrap?: boolean };

export function ConversationScreen({
    children,
    backgroundColor = colorSurfacePrimary,
    pad = false,
    noWrap = false,
}: ConversationScreenProps) {
    const s = ConversationScreenStyle;
    const embedded = useIsEmbedded();
    const isInSidebar = useIsInSidebar();
    const [narrow, setNarrow] = useState(false);
    const useWrapper = !isInSidebar && !narrow && !noWrap;
    const hasEmulateWarning = window.location.hostname == 'localhost' && !embedded;

    return (
        <AdaptiveWidth breakpoint={540} setNarrow={setNarrow} style={{ flex: 1 }}>
            <ScrollView style={[s.scroller, useWrapper ? null : { backgroundColor }]}>
                {useWrapper && <Pad size={40} />}
                <View style={s.outer}>
                    <View style={[s.screen, pad ? { paddingHorizontal: 20 } : null, useWrapper ? s.wrapper : null]}>{children}</View>
                </View>
                {useWrapper && <Pad size={48} />}
                <NewPublicFooter />
                {hasEmulateWarning && <Pad size={36} />}
            </ScrollView>
        </AdaptiveWidth>
    );
}

export const ConversationScreenStyle = StyleSheet.create({
    wrapper: {
        borderRadius: 12,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        overflow: 'hidden',
    },
    scroller: {
        flex: 1,
        ...({ overscrollBehavior: 'none' } as any),
    },
    outer: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        backgroundColor: colorSurfacePrimary,
        flex: 1,
    },
    screen: {
        maxWidth: 600,
        flex: 1,
    },
});

type AdminScreenProps = {
    capability: string;
};

export function AdminScreen({ capability, children }: PropsWithChildren<AdminScreenProps>) {
    const hasCapability = useHasCapability(capability);
    if (!hasCapability) {
        return (
            <Banner>
                <UtilityText label={t('You do not have access to this feature')} />
            </Banner>
        );
    }
    return <>{children}</>;
}
type AdminScreenHeaderProps = {
    children: React.ReactNode;
};

function AdminScreenHeader({ children }: AdminScreenHeaderProps) {
    const s = AdminScreenStyle;
    return (
        <View style={s.header}>
            <View style={s.container}>
                <PadBox vert={40}>{children}</PadBox>
            </View>
        </View>
    );
}
type AdminScreenBodyProps = {
    children: React.ReactNode;
};

function AdminScreenBody({ children }: AdminScreenBodyProps) {
    const s = AdminScreenStyle;
    return <View style={s.container}>{children}</View>;
}

AdminScreen.Header = AdminScreenHeader;
AdminScreen.Body = AdminScreenBody;

export const AdminScreenStyle = StyleSheet.create({
    header: {
        backgroundColor: colorSurfaceHighlight,
    },
    container: {
        maxWidth: 1280 + 80,
        paddingLeft: 40,
        paddingRight: 40,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
});

export function NewPublicFooter() {
    const siloKey = useSiloKey();
    if (siloKey != 'np') return null;
    return (
        <Center>
            <HorizBox center>
                <UtilityText label={t('Made by ')} mono type='large' />
                <WebLinkTextButton color={colorTextAccent} mono text='New_ Public' type='large' url='https://newpublic.org' />
            </HorizBox>
            <Pad size={80} />
        </Center>
    );
}
